const isElectronRuntime = () => Boolean(window?.electronAPI?.isElectron && window?.electronAPI?.store);

const readFromLocalStorage = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

const writeToLocalStorage = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    // Ignore storage errors to avoid auth flow breakage.
  }
};

const removeFromLocalStorage = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    // Ignore storage errors to avoid auth flow breakage.
  }
};

export const getStoredToken = async () => {
  return getStoredTokenSync();
};

export const getStoredUser = async () => {
  return getStoredUserSync();
};

export const setStoredAuth = async ({ token, user }) => {
  writeToLocalStorage('token', token);
  writeToLocalStorage('user', JSON.stringify(user));

  if (isElectronRuntime()) {
    await window.electronAPI.store.set('token', token);
    await window.electronAPI.store.set('user', user);
  }
};

// All localStorage keys that are scoped to a single user's session.
// These must be wiped on logout AND at the start of every login so that a
// new user never inherits stale POS data from a previous owner's session.
const SESSION_DATA_KEYS = [
  'selectedBranch',
  'selectedRestaurant',
  'pos_branches_cache',
];

/**
 * Clear all user-scoped session data (POS branch/restaurant cache).
 * Call on logout and at the start of login.
 */
export const clearSessionData = () => {
  SESSION_DATA_KEYS.forEach(key => removeFromLocalStorage(key));
};

export const clearStoredAuth = async () => {
  removeFromLocalStorage('token');
  removeFromLocalStorage('user');
  clearSessionData();

  if (isElectronRuntime()) {
    await window.electronAPI.store.delete('token');
    await window.electronAPI.store.delete('user');
  }
};

export const hydrateAuthFromElectronStore = async () => {
  if (!isElectronRuntime()) {
    return;
  }

  const existingToken = readFromLocalStorage('token');
  const existingUser = readFromLocalStorage('user');
  if (existingToken && existingUser) {
    return;
  }

  const token = await window.electronAPI.store.get('token');
  const user = await window.electronAPI.store.get('user');

  if (typeof token === 'string') {
    writeToLocalStorage('token', token);
  }
  if (user && typeof user === 'object') {
    writeToLocalStorage('user', JSON.stringify(user));
  }
};

export const getStoredTokenSync = () => {
  return readFromLocalStorage('token');
};

export const getStoredUserSync = () => {
  const user = readFromLocalStorage('user');
  if (!user) {
    return null;
  }

  try {
    return JSON.parse(user);
  } catch (error) {
    return null;
  }
};
